import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from './api';
import IncomingCall from './IncomingCall';
import ActiveCall from './ActiveCall';
import VideoCall from './VideoCall';
import './CallPage.css';

function CallPage() {
  const { callId } = useParams();
  const [callState, setCallState] = useState('loading'); // loading, incoming, active, ended, error
  const [callData, setCallData] = useState(null);
  const [error, setError] = useState(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!loadedRef.current) {
      loadedRef.current = true;
      loadCallData();
    }
  }, [callId]);

  const loadCallData = async () => {
    try {
      const response = await axios.get(`/api/calls/web/${callId}`);
      setCallData(response.data);
      setCallState('incoming');
    } catch (error) {
      console.error('Error loading call:', error);
      setError(error.response?.data?.error || 'Call not found');
      setCallState('error');
    }
  };

  const handleAccept = () => {
    setCallState('active');
    // Log that call was answered
    axios.post(`/api/calls/web/${callId}/answered`).catch(console.error);
  };

  const handleDecline = () => {
    setCallState('ended');
    // Log that call was declined
    axios.post(`/api/calls/web/${callId}/declined`).catch(console.error);
  };

  const handleCallEnd = () => {
    setCallState('ended');
    // Log that call was completed
    axios.post(`/api/calls/web/${callId}/completed`).catch(console.error);
  };

  const handleReplay = () => {
    setCallState('incoming');
  };

  if (callState === 'loading') {
    return (
      <div className="call-page loading">
        <div className="spinner"></div>
        <p>Loading call...</p>
      </div>
    );
  }

  if (callState === 'error') {
    return (
      <div className="call-page error">
        <div className="error-content">
          <span className="error-icon">❌</span>
          <h2>Call Not Available</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (callState === 'incoming') {
    return (
      <div className="call-page">
        <IncomingCall
          callerInfo={{
            callerType: callData.caller,
            childName: callData.childName
          }}
          onAccept={handleAccept}
          onDecline={handleDecline}
        />
      </div>
    );
  }

  if (callState === 'active') {
    // Use VideoCall if video URL is available, otherwise use AudioCall
    if (callData.videoUrl) {
      return (
        <div className="call-page">
          <VideoCall
            callerInfo={{
              callerType: callData.caller,
              childName: callData.childName
            }}
            videoUrl={callData.videoUrl}
            audioUrls={callData.audioUrls}
            onCallEnd={handleCallEnd}
          />
        </div>
      );
    }

    return (
      <div className="call-page">
        <ActiveCall
          callerInfo={{
            callerType: callData.caller,
            childName: callData.childName
          }}
          audioUrls={callData.audioUrls}
          onCallEnd={handleCallEnd}
        />
      </div>
    );
  }

  if (callState === 'ended') {
    return (
      <div className="call-page ended">
        <div className="call-ended-content">
          <span className="ended-icon">✅</span>
          <h2>Call Ended</h2>
          <p>Hope you enjoyed talking with {callData?.caller === 'SANTA' ? 'Santa' : callData?.caller === 'ELF' || callData?.caller === 'ELF_MALE' ? 'Jingle Jack' : callData?.caller === 'ELF_FEMALE' ? 'Snowdrop Ellie' : 'Mrs. Claus'}!</p>
          <div className="ended-actions">
            <button onClick={handleReplay} className="replay-btn">
              🔄 Listen Again
            </button>
            <button onClick={() => window.close()} className="close-btn">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default CallPage;
